import { fmtTL } from "@/components/StatusBadge";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plug, Loader2, CheckCircle2, XCircle, RefreshCw, PackageCheck, ShoppingBag, Save, Zap, Globe, FileText, Plus, Trash2, Store, AlertTriangle, ListChecks, Download, Brain, CreditCard, Lock } from "lucide-react";
import EDocWizardV2 from "@/components/EDocWizardV2";
import AiIntegration from "@/components/AiIntegration";
import PaymentCenter from "@/components/PaymentCenter";

export default function Integrations() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ supplier_id: "", api_key: "", api_secret: "", company_name: "", environment: "prod", scope: "stock_price" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [orders, setOrders] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [secTab, setSecTab] = useState("ecommerce");

  // ---- n11 (pazaryeri, canlı REST) ----
  const [n11, setN11] = useState(null);
  const [n11Form, setN11Form] = useState({ app_key: "", app_secret: "", company_name: "", scope: "stock_price" });
  const [n11Saving, setN11Saving] = useState(false);
  const [n11Testing, setN11Testing] = useState(false);
  const [n11TestResult, setN11TestResult] = useState(null);
  const [n11Syncing, setN11Syncing] = useState(false);
  const [n11Orders, setN11Orders] = useState(null);
  const [n11LoadingOrders, setN11LoadingOrders] = useState(false);
  const [importing, setImporting] = useState("");

  // ---- Ticimax (kendi site) ----
  const [tx, setTx] = useState(null);
  const [txForm, setTxForm] = useState({ domain: "https://www.grootmarket.com", uye_kodu: "", scope: "stock_price", enabled: true });
  const [txSaving, setTxSaving] = useState(false);
  const [txTesting, setTxTesting] = useState(false);
  const [txTestResult, setTxTestResult] = useState(null);
  const [txSyncing, setTxSyncing] = useState(false);
  const [txOrders, setTxOrders] = useState(null);
  const [txLoadingOrders, setTxLoadingOrders] = useState(false);
  const [txPreview, setTxPreview] = useState(null);
  const [txLoadingPreview, setTxLoadingPreview] = useState(false);


  const load = () => {
    api.get("/integrations/trendyol").then((r) => {
      setS(r.data);
      setForm((f) => ({ ...f, supplier_id: r.data.supplier_id || "", company_name: r.data.company_name || "", environment: r.data.environment || "prod", scope: r.data.scope || "stock_price" }));
    }).catch(() => {});
    api.get("/integrations/ticimax").then((r) => {
      setTx(r.data);
      setTxForm((f) => ({ ...f, domain: r.data.domain || "https://www.grootmarket.com", scope: r.data.scope || "stock_price", enabled: r.data.enabled !== false }));
      if (r.data.scope !== "orders") {
        api.get("/integrations/ticimax/listing-preview").then((p) => setTxPreview(p.data)).catch(() => {});
      }
    }).catch(() => {});
    api.get("/integrations/n11").then((r) => {
      setN11(r.data);
      setN11Form((f) => ({ ...f, company_name: r.data.company_name || "", scope: r.data.scope || "stock_price" }));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/integrations/trendyol", form);
      toast.success("Trendyol ayarları kaydedildi");
      setForm((f) => ({ ...f, api_key: "", api_secret: "" }));
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true); setTestResult(null);
    try { const { data } = await api.post("/integrations/trendyol/test"); setTestResult(data); }
    catch (err) { setTestResult({ ok: false, message: apiError(err.response?.data?.detail) || "Test başarısız" }); }
    finally { setTesting(false); }
  };
  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/integrations/trendyol/sync-stock"); toast.success(`${data.sent} ürün Trendyol'a gönderildi`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Senkron başarısız"); }
    finally { setSyncing(false); }
  };
  const fetchOrders = async () => {
    setLoadingOrders(true);
    try { const { data } = await api.get("/integrations/trendyol/orders"); setOrders(data.orders || []); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Siparişler alınamadı"); setOrders([]); }
    finally { setLoadingOrders(false); }
  };

  const txSave = async () => {
    setTxSaving(true);
    try {
      await api.post("/integrations/ticimax", txForm);
      toast.success("Ticimax ayarları kaydedildi");
      setTxForm((f) => ({ ...f, uye_kodu: "" }));
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setTxSaving(false); }
  };
  const txTest = async () => {
    setTxTesting(true); setTxTestResult(null);
    try { const { data } = await api.post("/integrations/ticimax/test"); setTxTestResult(data); }
    catch (err) { setTxTestResult({ ok: false, message: apiError(err.response?.data?.detail) || "Test başarısız" }); }
    finally { setTxTesting(false); }
  };
  const txSync = async () => {
    setTxSyncing(true);
    try {
      const { data } = await api.post("/integrations/ticimax/sync-stock");
      toast.success(`${data.sent} ürün grootmarket.com'a gönderildi${data.skipped ? ` · ${data.skipped} ürün yayına uygun değil (atlandı)` : ""}`);
      load();
    }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Senkron başarısız"); }
    finally { setTxSyncing(false); }
  };
  const txLoadPreview = async () => {
    setTxLoadingPreview(true);
    try { const { data } = await api.get("/integrations/ticimax/listing-preview"); setTxPreview(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Önizleme alınamadı"); }
    finally { setTxLoadingPreview(false); }
  };
  const txFetchOrders = async () => {
    setTxLoadingOrders(true);
    try { const { data } = await api.get("/integrations/ticimax/orders"); setTxOrders(data.orders || []); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Siparişler alınamadı"); setTxOrders([]); }
    finally { setTxLoadingOrders(false); }
  };

  const n11Save = async () => {
    setN11Saving(true);
    try {
      await api.post("/integrations/n11", n11Form);
      toast.success("n11 ayarları kaydedildi");
      setN11Form((f) => ({ ...f, app_key: "", app_secret: "" }));
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setN11Saving(false); }
  };
  const n11Test = async () => {
    setN11Testing(true); setN11TestResult(null);
    try { const { data } = await api.post("/integrations/n11/test"); setN11TestResult(data); }
    catch (err) { setN11TestResult({ ok: false, message: apiError(err.response?.data?.detail) || "Test başarısız" }); }
    finally { setN11Testing(false); }
  };
  const n11Sync = async () => {
    setN11Syncing(true);
    try { const { data } = await api.post("/integrations/n11/sync-stock"); toast.success(`${data.sent} ürün n11'e gönderildi`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Senkron başarısız"); }
    finally { setN11Syncing(false); }
  };
  const n11FetchOrders = async () => {
    setN11LoadingOrders(true);
    try { const { data } = await api.get("/integrations/n11/orders"); setN11Orders(data.orders || []); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Siparişler alınamadı"); setN11Orders([]); }
    finally { setN11LoadingOrders(false); }
  };
  const importOrders = async (provider) => {
    setImporting(provider);
    try {
      const { data } = await api.post(`/marketplace/import-orders?provider=${provider}`);
      toast.success(`${data.imported} sipariş ERP'ye aktarıldı${data.duplicates ? ` · ${data.duplicates} zaten kayıtlı` : ""}${data.skipped?.length ? ` · ${data.skipped.length} atlandı` : ""}`);
      window.dispatchEvent(new Event("mp-imported"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Siparişler ERP'ye aktarılamadı"); }
    finally { setImporting(""); }
  };


  const configured = s?.configured;
  const txConfigured = tx?.configured;

  return (
    <div className="fade-in mx-auto max-w-5xl space-y-5" data-testid="integrations-page">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-orange"><Plug className="h-5 w-5 text-white" /></div>
        <div><p className="overline">E-Ticaret</p><h1 className="font-heading text-2xl font-bold text-slate-900">Entegrasyonlar</h1></div>
      </div>

      <div className="inline-flex rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200" data-testid="integrations-tabs">
        <button data-testid="int-tab-ecommerce" onClick={() => setSecTab("ecommerce")}
          className={`inline-flex items-center gap-1.5 rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${secTab === "ecommerce" ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>
          <ShoppingBag className="h-4 w-4" /> Pazaryeri & Site
        </button>
        <button data-testid="int-tab-edoc" onClick={() => setSecTab("edoc")}
          className={`inline-flex items-center gap-1.5 rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${secTab === "edoc" ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>
          <FileText className="h-4 w-4" /> E-Belge (GİB)
        </button>
        <button data-testid="int-tab-ai" onClick={() => setSecTab("ai")}
          className={`inline-flex items-center gap-1.5 rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${secTab === "ai" ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>
          <Brain className="h-4 w-4" /> AI Asistan
        </button>
        <button data-testid="int-tab-payments" onClick={() => setSecTab("payments")}
          className={`inline-flex items-center gap-1.5 rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${secTab === "payments" ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>
          <CreditCard className="h-4 w-4" /> Ödeme Merkezi
        </button>
      </div>

      {secTab === "payments" && <PaymentCenter />}

      {secTab === "ai" && <AiIntegration />}

      {/* ---------------- Sınırsız Pazaryeri Bağlantıları ---------------- */}
      {secTab === "ecommerce" && <MarketplaceConnections />}

      {/* ---------------- Piyasa Sapmasına Göre Toplu Fiyat ---------------- */}
      {secTab === "ecommerce" && <MarketPricePush />}

      {/* ---------------- Otomatik Stok/Fiyat Senkronu ---------------- */}
      {secTab === "ecommerce" && <DiaAutoSyncSection />}

      {/* ---------------- E-Belge (e-Fatura / e-Arşiv / e-İrsaliye) ---------------- */}
      {/* ---------------- E-Belge — Sağlayıcı-bağımsız Sihirbaz (v2) ---------------- */}
      {secTab === "edoc" && <EDocWizardV2 isAdmin={isAdmin} />}
      {/* ---------------- Ticimax (kendi site) ---------------- */}
      {secTab === "ecommerce" && (
      <div className="rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="ticimax-card">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-brand-green" />
            <span className="font-heading text-lg font-bold text-slate-800">Ticimax · Kendi Site</span>
            <span className="rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">grootmarket.com</span>
          </div>
          {tx && (
            <span data-testid="ticimax-status" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${txConfigured ? "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200" : "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200"}`}>
              {txConfigured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
              {txConfigured ? "Bağlantı ayarlı" : "Bağlantı bekleniyor"}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label>Mağaza Alan Adı (domain)</Label><Input data-testid="ticimax-domain" value={txForm.domain} onChange={(e) => setTxForm({ ...txForm, domain: e.target.value })} placeholder="https://www.grootmarket.com" className="mt-1" /></div>
          <div><Label>WS Yetki Kodu (ÜyeKodu)</Label><Input data-testid="ticimax-uyekodu" type="password" autoComplete="new-password" value={txForm.uye_kodu} onChange={(e) => setTxForm({ ...txForm, uye_kodu: e.target.value })} placeholder={tx?.uye_kodu_masked || "Panelden alınan yetki kodu"} className="mt-1" /></div>
          <div>
            <Label>Kapsam</Label>
            <select data-testid="ticimax-scope" value={txForm.scope} onChange={(e) => setTxForm({ ...txForm, scope: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="stock_price">Stok + Fiyat gönderme</option>
              <option value="orders">Sipariş okuma</option>
              <option value="both">Her ikisi</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex h-10 items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" data-testid="ticimax-enabled" checked={txForm.enabled} onChange={(e) => setTxForm({ ...txForm, enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
              Entegrasyon aktif
            </label>
          </div>
        </div>

        <p className="mt-3 text-[11px] text-slate-400">Yetki Kodu Ticimax panelinde <b>Ayarlar → WS Yetki Kodu Yönetimi → Yeni Ekle</b> ile üretilir. Yalnızca sunucuda güvenle saklanır, geri gösterilmez. Alan adının Ticimax'ta canlı ve Web Servis modülünün açık olması gerekir.</p>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="ticimax-save" onClick={txSave} disabled={txSaving} className="bg-brand-green text-white hover:bg-brand-green-600">
            {txSaving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Kaydet
          </Button>
          <Button data-testid="ticimax-test" onClick={txTest} disabled={txTesting || !txConfigured} variant="outline">
            {txTesting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Zap className="mr-1.5 h-4 w-4" />} Bağlantıyı Test Et
          </Button>
        </div>
        {txTestResult && (
          <div data-testid="ticimax-test-result" className={`mt-3 rounded-md px-3 py-2 text-sm ring-1 ring-inset ${txTestResult.ok ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-red-50 text-red-700 ring-red-200"}`}>{txTestResult.message}</div>
        )}

        <div className="mt-4 border-t border-slate-100 pt-4">
          <p className="mb-3 font-heading text-sm font-bold text-slate-800">Senkronizasyon</p>
          {!txConfigured && <p className="mb-3 text-sm text-slate-400">Önce domain ve WS Yetki Kodu'nu kaydedin.</p>}

          {(tx?.scope || "stock_price") !== "orders" && (
            <div className="mb-4 rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200" data-testid="ticimax-listing-summary">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><ListChecks className="h-4 w-4 text-brand-green" /> Yayın Önizleme</span>
                <Button data-testid="ticimax-preview-btn" onClick={txLoadPreview} disabled={txLoadingPreview} variant="ghost" size="sm" className="h-7 px-2 text-xs">
                  {txLoadingPreview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
              </div>
              {txPreview ? (
                <>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded bg-white p-2 ring-1 ring-inset ring-emerald-100"><div className="text-lg font-bold text-emerald-600" data-testid="ticimax-preview-listable">{txPreview.listable}</div><div className="text-[10px] text-slate-500">Gönderilecek</div></div>
                    <div className="rounded bg-white p-2 ring-1 ring-inset ring-slate-100"><div className="text-lg font-bold text-slate-600" data-testid="ticimax-preview-outofstock">{txPreview.out_of_stock}</div><div className="text-[10px] text-slate-500">Stokta yok</div></div>
                    <div className="rounded bg-white p-2 ring-1 ring-inset ring-amber-100"><div className="text-lg font-bold text-amber-600" data-testid="ticimax-preview-blocked">{txPreview.blocked}</div><div className="text-[10px] text-slate-500">Yayına uygun değil</div></div>
                  </div>
                  {txPreview.reason_counts && Object.keys(txPreview.reason_counts).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {Object.entries(txPreview.reason_counts).map(([r, c]) => (
                        <span key={r} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200"><AlertTriangle className="h-3 w-3" />{r}: {c}</span>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-[11px] text-slate-400">Stokta olmayan ürünler de gönderilir (site "stokta yok" gösterir). Pasif / barkodsuz / fiyatsız ürünler yayınlanmaz.</p>
                  {(txPreview.reason_counts?.["fiyat yok"] || 0) > 0 && (
                    <Button
                      data-testid="ticimax-goto-pricing"
                      onClick={() => navigate("/fiyat-bekleyenler")}
                      variant="outline"
                      size="sm"
                      className="mt-2 h-8 border-amber-300 text-xs text-amber-700 hover:bg-amber-50"
                    >
                      <AlertTriangle className="mr-1.5 h-3.5 w-3.5" />
                      {txPreview.reason_counts["fiyat yok"]} fiyatsız ürünü fiyatla → Fiyat Bekleyenler
                    </Button>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-400">Yayınlanacak ürün özetini görmek için yenile simgesine dokunun.</p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {(tx?.scope || "stock_price") !== "orders" && (
              <Button data-testid="ticimax-sync" onClick={txSync} disabled={txSyncing || !txConfigured} className="bg-brand-orange text-white hover:bg-orange-600">
                {txSyncing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PackageCheck className="mr-1.5 h-4 w-4" />} Stok & Fiyat Gönder
              </Button>
            )}
            {(tx?.scope || "stock_price") !== "stock_price" && (
              <Button data-testid="ticimax-orders" onClick={txFetchOrders} disabled={txLoadingOrders || !txConfigured} variant="outline">
                {txLoadingOrders ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Siparişleri Çek
              </Button>
            )}
            {(tx?.scope || "stock_price") !== "stock_price" && (
              <Button data-testid="ticimax-import-orders" onClick={() => importOrders("ticimax")} disabled={importing === "ticimax" || !txConfigured} variant="outline" className="border-brand-green text-brand-green hover:bg-emerald-50">
                {importing === "ticimax" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Siparişleri ERP'ye Aktar
              </Button>
            )}
          </div>
          {tx?.last_stock_sync && <p className="mt-2 text-[11px] text-slate-400">Son stok senkronu: {new Date(tx.last_stock_sync).toLocaleString("tr-TR")} · {tx.last_stock_count} ürün</p>}
          {tx?.last_order_pull && <p className="mt-1 text-[11px] text-slate-400">Son sipariş çekme: {new Date(tx.last_order_pull).toLocaleString("tr-TR")}</p>}
          {txOrders && (
            <div className="mt-3" data-testid="ticimax-orders-list">
              {txOrders.length === 0 ? <p className="text-sm text-slate-400">Sipariş bulunamadı.</p> : (
                <div className="space-y-1.5">
                  {txOrders.slice(0, 15).map((o, i) => (
                    <div key={i} className="flex items-center justify-between rounded-sm bg-slate-50 px-3 py-2 text-sm">
                      <span className="font-medium text-slate-700">{o.orderNumber || o.id}</span>
                      <span className="text-slate-500">{o.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Sabit Trendyol/n11 kartları kaldırıldı — artık 'Pazaryeri Bağlantıları' altında dinamik yönetiliyor (ekle/düzenle/sil). */}

      {/* ---------------- Pazaryeri Siparişleri (ERP'ye aktarılanlar) ---------------- */}
      {secTab === "ecommerce" && <><MarketplaceOrdersImported /><UnmatchedQueue /></>}
    </div>
  );
}

// ============ Sınırsız Pazaryeri Bağlantıları ============
function MarketplaceConnections() {
  const [channels, setChannels] = useState([]);
  const [conns, setConns] = useState([]);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [nc, setNc] = useState({ channel: "", name: "", scope: "stock_price", config: {} });
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState({});
  const [results, setResults] = useState({});
  const [drafts, setDrafts] = useState({});
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const importConnOrders = async (c) => {
    setImportingId(c.id);
    try {
      const { data } = await api.post(`/marketplace/import-orders?provider=${encodeURIComponent(c.channel)}`);
      toast.success(`${data.imported ?? 0} sipariş ERP'ye aktarıldı${data.duplicates ? ` · ${data.duplicates} zaten vardı` : ""}`);
      window.dispatchEvent(new Event("mp-imported"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Sipariş aktarımı başarısız"); }
    finally { setImportingId(null); }
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/marketplace/channels"),
      api.get("/marketplace/connections"),
      api.get("/marketplace/listing-check"),
    ]).then(([c, cn, l]) => {
      setChannels(c.data || []); setConns(cn.data || []); setListing(l.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const chan = (key) => channels.find((c) => c.key === key);
  const ncChan = chan(nc.channel);
  const upd = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));
  const updCfg = (id, f, v) => setDrafts((d) => { const cur = d[id] || {}; return { ...d, [id]: { ...cur, config: { ...(cur.config || {}), [f]: v } } }; });
  const dget = (c, k, def) => (drafts[c.id]?.[k] !== undefined ? drafts[c.id][k] : def);

  const addConn = async () => {
    if (!nc.channel) { toast.error("Önce bir pazaryeri seçin"); return; }
    setAdding(true);
    try {
      await api.post("/marketplace/connections", { channel: nc.channel, name: nc.name, scope: nc.scope, config: nc.config });
      toast.success("Bağlantı eklendi");
      setNc({ channel: "", name: "", scope: "stock_price", config: {} }); setShowAdd(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Eklenemedi"); }
    finally { setAdding(false); }
  };
  const saveConn = async (c) => {
    setBusy((b) => ({ ...b, [c.id]: "save" }));
    const d = drafts[c.id] || {};
    try {
      await api.put(`/marketplace/connections/${c.id}`, { name: d.name ?? c.name, scope: d.scope ?? c.scope, enabled: c.enabled, config: d.config || {} });
      toast.success("Güncellendi");
      setDrafts((x) => { const n = { ...x }; delete n[c.id]; return n; }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Güncellenemedi"); }
    finally { setBusy((b) => ({ ...b, [c.id]: null })); }
  };
  const testConn = async (c) => {
    setBusy((b) => ({ ...b, [c.id]: "test" })); setResults((r) => ({ ...r, [c.id]: null }));
    try { const { data } = await api.post(`/marketplace/connections/${c.id}/test`); setResults((r) => ({ ...r, [c.id]: data })); }
    catch (err) { setResults((r) => ({ ...r, [c.id]: { ok: false, message: apiError(err.response?.data?.detail) || "Test başarısız" } })); }
    finally { setBusy((b) => ({ ...b, [c.id]: null })); }
  };
  const syncConn = async (c) => {
    setBusy((b) => ({ ...b, [c.id]: "sync" }));
    try { const { data } = await api.post(`/marketplace/connections/${c.id}/sync-stock`); toast.success(`${data.sent} ürün gönderildi · ${data.skipped} atlandı`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Senkron başarısız"); }
    finally { setBusy((b) => ({ ...b, [c.id]: null })); }
  };
  const verifyConn = async (c) => {
    setBusy((b) => ({ ...b, [c.id]: "verify" })); setResults((r) => ({ ...r, [c.id]: null }));
    try {
      const { data: t } = await api.post(`/marketplace/connections/${c.id}/test`);
      if (!t.ok) { setResults((r) => ({ ...r, [c.id]: t })); toast.error("Bağlantı testi başarısız — API anahtarlarını kontrol edin"); return; }
      let msg = "Bağlantı ✓";
      try { const { data: s } = await api.post(`/marketplace/connections/${c.id}/sync-stock`); msg += ` · ${s.sent} ürün gönderildi`; } catch { msg += " · stok gönderilemedi"; }
      if (c.scope === "orders" || c.scope === "both") {
        try { const { data: o } = await api.post(`/marketplace/import-orders?provider=${c.channel}`); msg += ` · ${o.imported ?? 0} sipariş çekildi`; } catch { msg += " · sipariş çekilemedi"; }
      }
      setResults((r) => ({ ...r, [c.id]: { ok: true, message: msg } }));
      toast.success("Canlı doğrulama tamamlandı ✓");
      load();
    } catch (err) {
      setResults((r) => ({ ...r, [c.id]: { ok: false, message: apiError(err.response?.data?.detail) || "Doğrulama başarısız" } }));
      toast.error("Canlı doğrulama başarısız");
    } finally { setBusy((b) => ({ ...b, [c.id]: null })); }
  };
  const toggleEnabled = async (c, val) => {
    setConns((cs) => cs.map((x) => (x.id === c.id ? { ...x, enabled: val } : x)));
    try { await api.put(`/marketplace/connections/${c.id}`, { enabled: val, name: c.name, scope: c.scope }); toast.success(val ? "Bağlantı aktifleştirildi" : "Bağlantı pasifleştirildi"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Güncellenemedi"); load(); }
  };
  const confirmDel = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try { await api.delete(`/marketplace/connections/${delTarget.id}`); toast.success("Bağlantı silindi"); setDelTarget(null); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Silinemedi"); }
    finally { setDeleting(false); }
  };

  const scopeSel = (val, onCh, tid) => (
    <select data-testid={tid} value={val} onChange={onCh} className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
      <option value="stock_price">Stok + Fiyat gönderme</option>
      <option value="orders">Sipariş okuma</option>
      <option value="both">Her ikisi</option>
    </select>
  );

  return (
    <div className="rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="marketplaces-card">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Store className="h-5 w-5 text-brand-orange" />
          <span className="font-heading text-lg font-bold text-slate-800">Pazaryeri Bağlantıları</span>
          <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">sınırsız</span>
        </div>
        <Button data-testid="mp-add-btn" size="sm" onClick={() => setShowAdd((v) => !v)} className="bg-brand-green text-white hover:bg-brand-green-600">
          <Plus className="mr-1 h-4 w-4" /> Yeni Bağlantı
        </Button>
      </div>

      <p className="mb-4 text-xs text-slate-500" data-testid="mp-manage-note">
        Trendyol, n11, Hepsiburada, Yemeksepeti, Getir Yemek, GetirÇarşı, Shopify ve daha fazlası — hepsini buradan <b>ekleyin, düzenleyin, silin</b>. Sabit/varsayılan pazaryeri yoktur; yalnızca kullandıklarınızı ekleyin.
      </p>

      {listing && (
        <div className="mb-4 rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200" data-testid="mp-listing-summary">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="flex items-center gap-1.5 font-semibold text-slate-700"><ListChecks className="h-4 w-4 text-brand-green" /> Yayına uygun: <b className="text-emerald-700">{listing.listable}</b></span>
            <span className="text-slate-500">Toplam ürün: {listing.total}</span>
            <span className="flex items-center gap-1.5 text-amber-700"><AlertTriangle className="h-4 w-4" /> Yayınlanamaz: <b>{listing.blocked}</b></span>
            {Object.entries(listing.reason_counts || {}).map(([k, v]) => (
              <span key={k} className="rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-500 ring-1 ring-inset ring-slate-200">{k}: {v}</span>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-slate-400">Kural: fiyatı 0 olan, stoğu olmayan veya pasif ürünler pazaryerlerine <b>gönderilmez</b>. Bu ürünleri düzelttikçe otomatik yayına uygun hale gelir.</p>
        </div>
      )}

      {showAdd && (
        <div className="mb-4 rounded-md border border-dashed border-brand-green/40 bg-brand-green/5 p-4" data-testid="mp-add-form">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Pazaryeri / Kanal</Label>
              <select data-testid="mp-add-channel" value={nc.channel} onChange={(e) => setNc({ ...nc, channel: e.target.value, config: {} })} className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                <option value="">Seçin…</option>
                {channels.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div><Label>Görünen Ad (opsiyonel)</Label><Input data-testid="mp-add-name" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} placeholder={ncChan?.label || "Örn. Trendyol Mağaza 2"} className="mt-1" /></div>
            {(ncChan?.fields || []).map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input data-testid={`mp-add-${f.key}`} type={f.secret ? "password" : "text"} value={nc.config[f.key] || ""} onChange={(e) => setNc({ ...nc, config: { ...nc.config, [f.key]: e.target.value } })} className="mt-1" />
              </div>
            ))}
            <div><Label>Kapsam</Label>{scopeSel(nc.scope, (e) => setNc({ ...nc, scope: e.target.value }), "mp-add-scope")}</div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button data-testid="mp-add-save" onClick={addConn} disabled={adding || !nc.channel} className="bg-brand-green text-white hover:bg-brand-green-600">
              {adding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Ekle
            </Button>
            <Button data-testid="mp-add-cancel" variant="outline" onClick={() => { setShowAdd(false); setNc({ channel: "", name: "", scope: "stock_price", config: {} }); }}>Vazgeç</Button>
          </div>
        </div>
      )}

      {loading && <p className="py-6 text-center text-sm text-slate-400">Yükleniyor…</p>}
      {!loading && conns.length === 0 && !showAdd && (
        <p className="py-6 text-center text-sm text-slate-400" data-testid="mp-empty">Henüz pazaryeri bağlantısı yok. "Yeni Bağlantı" ile Trendyol, Hepsiburada, GetirÇarşı, Shopify ve daha fazlasını ekleyin.</p>
      )}

      {conns.map((c) => {
        const ch = chan(c.channel);
        const b = busy[c.id];
        const res = results[c.id];
        return (
          <div key={c.id} data-testid={`mp-conn-${c.id}`} className="mb-3 rounded-md p-4 ring-1 ring-inset ring-slate-200">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-heading text-sm font-bold text-slate-800">{c.name}</span>
                <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">{c.channel_label}</span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.configured ? "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200" : "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200"}`}>
                  {c.configured ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} {c.configured ? "Bilgiler tam" : "Eksik bilgi"}
                </span>
                {c.status === "ok" && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">test ✓</span>}
                {c.status === "error" && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">test ✕</span>}
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid={`mp-enabled-${c.id}`} checked={c.enabled} onChange={(e) => toggleEnabled(c, e.target.checked)} className="h-4 w-4 rounded border-slate-300" /> Aktif
              </label>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label>Görünen Ad</Label><Input data-testid={`mp-name-${c.id}`} value={dget(c, "name", c.name)} onChange={(e) => upd(c.id, { name: e.target.value })} className="mt-1" /></div>
              <div><Label>Kapsam</Label>{scopeSel(dget(c, "scope", c.scope), (e) => upd(c.id, { scope: e.target.value }), `mp-scope-${c.id}`)}</div>
              {(ch?.fields || []).map((f) => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  <Input data-testid={`mp-cfg-${c.id}-${f.key}`} type={f.secret ? "password" : "text"} value={drafts[c.id]?.config?.[f.key] ?? ""} onChange={(e) => updCfg(c.id, f.key, e.target.value)} placeholder={c.config?.[f.key] || f.label} className="mt-1" />
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button data-testid={`mp-save-${c.id}`} size="sm" onClick={() => saveConn(c)} disabled={b === "save"} className="bg-brand-green text-white hover:bg-brand-green-600">
                {b === "save" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Kaydet
              </Button>
              <Button data-testid={`mp-test-${c.id}`} size="sm" variant="outline" onClick={() => testConn(c)} disabled={b === "test"}>
                {b === "test" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Zap className="mr-1 h-4 w-4" />} Test Et
              </Button>
              <Button data-testid={`mp-sync-${c.id}`} size="sm" onClick={() => syncConn(c)} disabled={b === "sync" || !c.enabled || !c.configured} className="bg-brand-orange text-white hover:bg-orange-600">
                {b === "sync" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <PackageCheck className="mr-1 h-4 w-4" />} Stok & Fiyat Gönder
              </Button>
              <Button data-testid={`mp-verify-${c.id}`} size="sm" onClick={() => verifyConn(c)} disabled={b === "verify" || !c.configured} className="bg-emerald-600 text-white hover:bg-emerald-700">
                {b === "verify" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Zap className="mr-1 h-4 w-4" />} Canlı Doğrula (tek tık)
              </Button>
              {(c.scope === "orders" || c.scope === "both") && (
                <Button data-testid={`mp-import-orders-${c.id}`} size="sm" variant="outline" onClick={() => importConnOrders(c)} disabled={importingId === c.id || !c.configured} className="border-brand-green text-brand-green hover:bg-emerald-50">
                  {importingId === c.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Siparişleri Aktar
                </Button>
              )}
              <Button data-testid={`mp-del-${c.id}`} size="sm" variant="outline" onClick={() => setDelTarget(c)} className="text-red-600 hover:bg-red-50">
                <Trash2 className="mr-1 h-4 w-4" /> Sil
              </Button>
            </div>
            {c.last_stock_sync && <p className="mt-2 text-[11px] text-slate-400">Son senkron: {new Date(c.last_stock_sync).toLocaleString("tr-TR")} · {c.last_stock_count} ürün</p>}
            {res && <div data-testid={`mp-test-result-${c.id}`} className={`mt-2 rounded-md px-3 py-2 text-sm ring-1 ring-inset ${res.ok ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-red-50 text-red-700 ring-red-200"}`}>{res.message}</div>}
          </div>
        );
      })}

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="mp-del-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Bağlantıyı sil</DialogTitle>
            <DialogDescription>
              <b>{delTarget?.name}</b> ({delTarget?.channel_label}) bağlantısı kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)} disabled={deleting}>Vazgeç</Button>
            <Button data-testid="mp-del-confirm" onClick={confirmDel} disabled={deleting} className="bg-red-600 text-white hover:bg-red-700">
              {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />} Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Piyasa Sapmasına Göre Toplu Fiyat Gönderimi ============
function MarketPricePush() {
  const [conns, setConns] = useState([]);
  const [connId, setConnId] = useState("");
  const [threshold, setThreshold] = useState(10);
  const [strategy, setStrategy] = useState("match");
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [marginOpen, setMarginOpen] = useState(false);
  const [margin, setMargin] = useState({ global_pct: 10, categories: {} });
  const [cats, setCats] = useState([]);
  const [savingMargin, setSavingMargin] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [retry, setRetry] = useState(null);

  const loadBlocked = async () => {
    setBlockedLoading(true);
    try { const { data } = await api.get("/marketplace/margin-lock/blocked", { params: { strategy } }); setBlocked(data); }
    catch { setBlocked(null); }
    finally { setBlockedLoading(false); }
  };
  const openMargin = () => { setMarginOpen(true); loadBlocked(); };

  useEffect(() => {
    api.get("/marketplace/connections").then((r) => {
      const list = (r.data || []).filter((c) => ["trendyol", "n11"].includes(c.channel));
      setConns(list);
      const ty = list.find((c) => c.channel === "trendyol") || list[0];
      if (ty) setConnId(ty.id);
    }).catch(() => {});
    api.get("/marketplace/margin-lock").then((r) => setMargin({ global_pct: r.data?.global_pct ?? 10, categories: r.data?.categories || {} })).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    let retryId = "";
    try { retryId = sessionStorage.getItem("groot_market_price_retry") || ""; } catch { /* storage unavailable */ }
    if (!retryId) return () => { active = false; };
    api.get(`/marketplace/price-push-retries/${retryId}`).then(({ data }) => { if (active) setRetry({ id: retryId, ...data }); }).catch(() => { try { sessionStorage.removeItem("groot_market_price_retry"); } catch { /* ignore */ } });
    return () => { active = false; };
  }, []);

  const setCatMargin = (name, val) => setMargin((m) => {
    const c = { ...(m.categories || {}) };
    if (val === "" || val == null) delete c[name]; else c[name] = val;
    return { ...m, categories: c };
  });
  const saveMargin = async () => {
    setSavingMargin(true);
    try {
      const { data } = await api.put("/marketplace/margin-lock", { global_pct: Number(margin.global_pct) || 0, categories: margin.categories });
      setMargin({ global_pct: data.global_pct, categories: data.categories || {} });
      toast.success("Kâr marjı kilidi kaydedildi");
      loadBlocked();
      if (rows) preview();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setSavingMargin(false); }
  };

  const targetChannel = conns.find((c) => c.id === connId)?.channel || "trendyol";
  const targetLabel = conns.find((c) => c.id === connId)?.channel_label || "Trendyol";

  const preview = async () => {
    setLoading(true); setRows(null); setSel(new Set());
    try {
      const { data } = await api.get("/marketplace/price-suggestions", { params: { threshold, strategy } });
      setRows(data.items || []);
      setSel(new Set((data.items || []).map((x) => x.id)));
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Sapmalar getirilemedi"); }
    finally { setLoading(false); }
  };

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel((s) => (rows && s.size === rows.length ? new Set() : new Set((rows || []).map((x) => x.id))));

  const apply = async () => {
    if (!sel.size) { toast.error("En az bir ürün seçin"); return; }
    setApplying(true);
    try {
      const { data } = await api.post("/marketplace/apply-price-suggestions", {
        connection_id: connId || undefined, channel: targetChannel,
        ids: [...sel], threshold, strategy, dry_run: false,
      });
      let msg = `${data.applied} ürünün fiyatı ERP'de güncellendi`;
      if (data.pushed) msg += ` · ${data.pushed} ürün ${targetLabel}'a gönderildi ✓`;
      toast.success(msg);
      if (data.push_error && data.retry_id) {
        setRetry({ id: data.retry_id, status: "pending", message: data.push_error, applied: data.applied });
        try { sessionStorage.setItem("groot_market_price_retry", data.retry_id); } catch { /* ignore */ }
        toast.error("ERP fiyatı kaydedildi; pazaryeri gönderimi tamamlanmadı.");
      } else {
        setRetry(null);
        try { sessionStorage.removeItem("groot_market_price_retry"); } catch { /* ignore */ }
      }
      preview();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Uygulanamadı"); }
    finally { setApplying(false); }
  };

  const allChecked = rows && rows.length > 0 && sel.size === rows.length;

  return (
    <div className="rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="market-price-push-card">
      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-brand-orange" />
        <span className="font-heading text-lg font-bold text-slate-800">Piyasa Sapmasına Göre Toplu Fiyat</span>
        <span className="rounded-sm bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold text-brand-orange">tek tık</span>
        <Button data-testid="mpp-margin-btn" size="sm" variant="outline" onClick={openMargin} className="ml-auto h-8">
          <Lock className="mr-1.5 h-3.5 w-3.5" /> Kâr Marjı Kilidi (%{margin.global_pct})
        </Button>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Piyasa taramasında fiyatı piyasadan sapan ürünleri bulur, önerilen (piyasaya uygun, maliyet+kâr korumalı) fiyatı hesaplar ve seçilenleri <b>tek tıkla pazaryerine</b> (Trendyol/n11) gönderir. Not: canlı gönderim için ilgili bağlantının API anahtarları kayıtlı olmalı; anahtar yoksa fiyatlar yalnız ERP'de güncellenir.
      </p>

      {retry && (
        <div data-testid="mpp-retry-status" className={`mb-4 rounded-md p-3 text-sm ring-1 ring-inset ${retry.status === "sent" ? "bg-emerald-50 text-emerald-800 ring-emerald-200" : "bg-amber-50 text-amber-900 ring-amber-200"}`}>
          <div className="font-semibold">{retry.status === "sent" ? "Pazaryeri gönderimi tamamlandı" : "ERP fiyatı kaydedildi; dış gönderim başarısız"}</div>
          <p className="mt-1 text-xs">{retry.message}</p>
          {retry.skipped > 0 && <p className="mt-1 text-xs">{retry.skipped} ürünün ERP fiyatı değiştiği için eski fiyat snapshot'ı gönderilmedi.</p>}
        </div>
      )}
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div>
          <Label>Hedef Pazaryeri</Label>
          <select data-testid="mpp-target" value={connId} onChange={(e) => setConnId(e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
            {conns.length === 0 && <option value="">Trendyol (bağlantı yok — sadece ERP)</option>}
            {conns.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.channel_label}</option>)}
          </select>
        </div>
        <div>
          <Label>Sapma Eşiği %</Label>
          <Input data-testid="mpp-threshold" type="number" min="0" max="90" value={threshold}
            onChange={(e) => setThreshold(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label>Strateji</Label>
          <select data-testid="mpp-strategy" value={strategy} onChange={(e) => setStrategy(e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
            <option value="match">Piyasa ortalamasına eşle</option>
            <option value="undercut">En ucuzun %1 altı (rekabetçi)</option>
          </select>
        </div>
        <div className="flex items-end">
          <Button data-testid="mpp-preview" onClick={preview} disabled={loading} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
            {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Sapmaları Getir
          </Button>
        </div>
      </div>

      {rows && rows.length === 0 && (
        <p className="py-6 text-center text-sm text-slate-400" data-testid="mpp-empty">Bu eşikte fiyat sapması bulunamadı. Önce Pazar Araştırması → Kaynaklar & Ayarlar'dan tarama çalıştırın veya eşiği düşürün.</p>
      )}

      {rows && rows.length > 0 && (
        <>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs text-slate-500" data-testid="mpp-count"><b>{rows.length}</b> sapmalı ürün · <b>{sel.size}</b> seçili</span>
            <Button data-testid="mpp-apply" onClick={apply} disabled={applying || sel.size === 0} className="bg-brand-orange text-white hover:bg-orange-600">
              {applying ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PackageCheck className="mr-1.5 h-4 w-4" />} Seçilenleri Uygula &amp; {targetLabel}'a Gönder
            </Button>
          </div>
          <div className="max-h-96 overflow-y-auto rounded-md ring-1 ring-inset ring-slate-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-2 py-2"><input type="checkbox" data-testid="mpp-select-all" checked={allChecked} onChange={toggleAll} className="h-4 w-4 accent-brand-green" /></th>
                  <th className="px-2 py-2">Ürün</th>
                  <th className="px-2 py-2 text-right">Mevcut</th>
                  <th className="px-2 py-2 text-right">Piyasa Ort.</th>
                  <th className="px-2 py-2 text-right">Önerilen</th>
                  <th className="px-2 py-2 text-right">Sapma</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`mpp-row-${r.id}`} className="hover:bg-slate-50">
                    <td className="px-2 py-1.5"><input type="checkbox" data-testid={`mpp-check-${r.id}`} checked={sel.has(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4 accent-brand-green" /></td>
                    <td className="px-2 py-1.5"><span className="block max-w-xs truncate text-slate-800">{r.name}</span><span className="text-[10px] text-slate-400">{r.sku}</span></td>
                    <td className="px-2 py-1.5 text-right tabular text-slate-600">{fmtTL(r.current_price)}</td>
                    <td className="px-2 py-1.5 text-right tabular text-slate-600">{fmtTL(r.market_avg)}</td>
                    <td className="px-2 py-1.5 text-right tabular font-semibold text-brand-green">{fmtTL(r.suggested_price)}</td>
                    <td className="px-2 py-1.5 text-right">
                      {r.deviation_pct == null
                        ? <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-600">fiyat yok</span>
                        : <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold tabular ${r.deviation_pct > 0 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>{r.deviation_pct > 0 ? "+" : ""}{r.deviation_pct}%</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={marginOpen} onOpenChange={setMarginOpen}>
        <DialogContent data-testid="mpp-margin-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Kâr Marjı Kilidi</DialogTitle>
            <DialogDescription>Önerilen fiyat, maliyetin üstüne en az bu kâr yüzdesi eklenmeden düşmez. Kategori bazlı override girebilirsiniz (boş = global marj geçerli).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Global Asgari Kâr %</Label>
              <Input data-testid="mpp-margin-global" type="number" min="0" value={margin.global_pct} onChange={(e) => setMargin({ ...margin, global_pct: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Kategori Bazlı (opsiyonel)</Label>
              <div className="mt-1 max-h-60 space-y-1.5 overflow-y-auto pr-1" data-testid="mpp-margin-cats">
                {cats.map((c) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{c.name}</span>
                    <Input data-testid={`mpp-margin-cat-${c.id}`} type="number" min="0" value={margin.categories?.[c.name] ?? ""} onChange={(e) => setCatMargin(c.name, e.target.value)} placeholder={`${margin.global_pct}`} className="h-8 w-24" />
                    <span className="text-xs text-slate-400">%</span>
                  </div>
                ))}
                {cats.length === 0 && <p className="text-xs text-slate-400">Kategori bulunamadı.</p>}
              </div>
            </div>
            <div className="rounded-md bg-amber-50/60 p-3 ring-1 ring-inset ring-amber-200" data-testid="mpp-blocked-panel">
              <div className="mb-1.5 flex items-center gap-2">
                <Lock className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-semibold text-slate-800">Kilidin Devreye Girdiği Ürünler</span>
                <span data-testid="mpp-blocked-count" className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">{blockedLoading ? "…" : (blocked?.count ?? 0)}</span>
              </div>
              <p className="mb-2 text-[11px] text-slate-500">Piyasa fiyatı maliyet+asgari marjın altında kaldığı için fiyatı <b>tabana yükseltilen</b> ürünler — kâr bu ürünlerde korunuyor.</p>
              {blockedLoading ? (
                <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
              ) : (blocked?.items?.length ? (
                <div className="max-h-52 overflow-y-auto rounded-md ring-1 ring-inset ring-amber-100">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-amber-100/70 text-left text-[10px] text-amber-800">
                      <tr><th className="px-2 py-1.5">Ürün</th><th className="px-2 py-1.5 text-right">Maliyet</th><th className="px-2 py-1.5 text-right">Piyasa</th><th className="px-2 py-1.5 text-right">Taban</th><th className="px-2 py-1.5 text-right">Marj%</th></tr>
                    </thead>
                    <tbody className="divide-y divide-amber-50">
                      {blocked.items.map((r) => (
                        <tr key={r.id} data-testid={`mpp-blocked-row-${r.id}`}>
                          <td className="px-2 py-1.5"><span className="block max-w-[10rem] truncate text-slate-700">{r.name}</span></td>
                          <td className="px-2 py-1.5 text-right tabular text-slate-500">{fmtTL(r.cost)}</td>
                          <td className="px-2 py-1.5 text-right tabular text-slate-500">{fmtTL(r.market_avg)}</td>
                          <td className="px-2 py-1.5 text-right tabular font-semibold text-amber-700">{fmtTL(r.floor_price)}</td>
                          <td className="px-2 py-1.5 text-right tabular text-slate-500">{r.min_margin_pct}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-3 text-center text-[11px] text-slate-400" data-testid="mpp-blocked-empty">Bu marjda taban devreye giren ürün yok — tüm piyasa fiyatları maliyet+marjın üstünde.</p>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarginOpen(false)}>Vazgeç</Button>
            <Button data-testid="mpp-margin-save" onClick={saveMargin} disabled={savingMargin} className="bg-brand-green text-white hover:bg-brand-green-600">
              {savingMargin ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Pazaryeri Siparişleri (ERP'ye aktarılanlar) ============
function MarketplaceOrdersImported() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/marketplace/orders", { params: { limit: 30 } }).then((r) => setOrders(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="marketplace-orders-card">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Download className="h-5 w-5 text-brand-green" />
          <span className="font-heading text-lg font-bold text-slate-800">Pazaryeri Siparişleri</span>
          <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">ERP'ye aktarılan</span>
        </div>
        <Button data-testid="mp-orders-refresh" size="sm" variant="ghost" onClick={load} className="h-8 px-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>
      {!loading && orders.length === 0 && (
        <p className="py-6 text-center text-sm text-slate-400" data-testid="mp-orders-empty">Henüz ERP'ye aktarılmış pazaryeri siparişi yok. Pazaryeri bağlantısında (kapsam "Sipariş okuma" ise) "Siparişleri Aktar" ile çekin.</p>
      )}
      {orders.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-slate-500">
              <tr><th className="px-2 py-1.5">Fiş No</th><th className="px-2 py-1.5">Pazaryeri</th><th className="px-2 py-1.5">Sipariş No</th><th className="px-2 py-1.5 text-right">Kalem</th><th className="px-2 py-1.5 text-right">Tutar</th><th className="px-2 py-1.5">Tarih</th></tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`mp-order-${o.id}`} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 font-mono text-xs text-slate-700">{o.sale_no}</td>
                  <td className="px-2 py-1.5"><span className="rounded-sm bg-orange-50 px-1.5 py-0.5 text-[11px] font-semibold text-brand-orange">{o.label || o.provider}</span></td>
                  <td className="px-2 py-1.5 text-slate-600">{o.order_id}</td>
                  <td className="px-2 py-1.5 text-right tabular">{o.items}</td>
                  <td className="px-2 py-1.5 text-right tabular font-semibold">{fmtTL(o.total)}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-400">{o.created_at ? new Date(o.created_at).toLocaleString("tr-TR") : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ Eşleşmeyen Kalemler Onay Kuyruğu ============
function ProductPicker({ onPick, defaultQuery, testid }) {
  const [qs, setQs] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const search = (val) => {
    setQs(val);
    if (timer.current) clearTimeout(timer.current);
    if ((val || "").trim().length < 2) { setRes([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q: val.trim(), match: "contains", page_size: 6 } });
        setRes(data.items || []); setOpen(true);
      } catch { setRes([]); }
    }, 300);
  };
  return (
    <div className="relative">
      <input data-testid={`${testid}-input`} value={qs} onChange={(e) => search(e.target.value)}
        placeholder={`Ürün ara: ${(defaultQuery || "").slice(0, 24)}`}
        className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-green focus:outline-none" />
      {open && res.length > 0 && (
        <ul data-testid={`${testid}-results`} className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {res.map((p) => (
            <li key={p.id}>
              <button data-testid={`${testid}-opt-${p.id}`} onClick={() => { setOpen(false); setQs(p.name); onPick(p.id); }}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-emerald-50">
                <span className="text-slate-700">{p.name}</span>
                <span className="flex shrink-0 items-center gap-1 text-[11px] font-semibold text-brand-green"><CheckCircle2 className="h-3.5 w-3.5" /> Eşleştir</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UnmatchedQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/marketplace/unmatched", { params: { status: "pending", limit: 100 } })
      .then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("mp-imported", h);
    return () => window.removeEventListener("mp-imported", h);
  }, []);
  const resolve = async (id, productId) => {
    try {
      const { data } = await api.post(`/marketplace/unmatched/${id}/resolve`, { product_id: productId, create_alias: true });
      toast.success(`Eşleştirildi → ${data.product} (${data.sale_no})`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Eşleştirilemedi"); }
  };
  const ignore = async (id) => {
    try { await api.post(`/marketplace/unmatched/${id}/ignore`); toast.success("Kalem yoksayıldı"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "İşlem başarısız"); }
  };
  if (!loading && items.length === 0) return null;
  return (
    <div className="rounded-lg bg-white p-5 ring-1 ring-inset ring-amber-200" data-testid="unmatched-queue-card">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-amber-600" />
          <span className="font-heading text-lg font-bold text-slate-800">Eşleşmeyen Kalemler</span>
          <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700" data-testid="unmatched-count">{items.length} bekliyor</span>
        </div>
        <Button data-testid="unmatched-refresh" size="sm" variant="ghost" onClick={load} className="h-8 px-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>
      <p className="mb-3 text-xs text-slate-500">Pazaryeri siparişlerinde ERP ürünüyle otomatik eşleşmeyen kalemler. Doğru ürünü seçip eşleştirin — kalem satışa eklenir ve barkod bir daha otomatik tanınır.</p>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.id} data-testid={`unmatched-row-${it.id}`} className="rounded-md border border-slate-200 p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-medium text-slate-800">{it.name || "(adsız kalem)"}</div>
                <div className="text-[11px] text-slate-400">{it.label} · Sipariş {it.order_id} · {it.quantity} adet{it.barcode ? ` · Barkod ${it.barcode}` : ""}</div>
              </div>
              <Button data-testid={`unmatched-ignore-${it.id}`} size="sm" variant="ghost" onClick={() => ignore(it.id)} className="text-slate-400 hover:text-rose-600">
                <XCircle className="mr-1 h-3.5 w-3.5" /> Yoksay
              </Button>
            </div>
            <ProductPicker onPick={(pid) => resolve(it.id, pid)} defaultQuery={it.name} testid={`unmatched-picker-${it.id}`} />
          </li>
        ))}
      </ul>
    </div>
  );
}


// ============ Otomatik Stok/Fiyat Senkronu ============
function DiaAutoSyncSection() {
  const [auto, setAuto] = useState({ enabled: false, providers: ["trendyol", "n11", "ticimax"] });
  const [autoSaving, setAutoSaving] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);

  const load = () => {
    api.get("/integrations/auto-sync").then((r) => setAuto({ ...r.data, providers: Array.isArray(r.data?.providers) ? r.data.providers : ["trendyol", "n11", "ticimax"] })).catch(() => {});
  };
  useEffect(load, []);
  const provList = Array.isArray(auto.providers) ? auto.providers : [];

  const toggleProvider = (p) => {
    const cur = Array.isArray(auto.providers) ? auto.providers : [];
    const has = cur.includes(p);
    setAuto({ ...auto, providers: has ? cur.filter((x) => x !== p) : [...cur, p] });
  };
  const autoSave = async () => {
    setAutoSaving(true);
    try { const { data } = await api.post("/integrations/auto-sync", { enabled: auto.enabled, providers: provList, pull_orders: auto.pull_orders !== false }); setAuto({ ...auto, ...data }); toast.success("Otomatik senkron ayarı kaydedildi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); } finally { setAutoSaving(false); }
  };
  const autoRun = async () => {
    setAutoRunning(true);
    try { const { data } = await api.post("/integrations/auto-sync/run"); toast.success(data.skipped ? "Otomatik senkron kapalı — önce açın" : "Senkron çalıştı ✓"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Çalıştırılamadı"); } finally { setAutoRunning(false); }
  };

  return (
    <>
      <div className="rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="auto-sync-card">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-brand-green" />
            <span className="font-heading text-lg font-bold text-slate-800">Otomatik Stok/Fiyat Senkronu</span>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" data-testid="auto-sync-enabled" checked={!!auto.enabled} onChange={(e) => setAuto({ ...auto, enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
            Aç
          </label>
        </div>
        <p className="mb-3 text-xs text-slate-500">Açıldığında, seçili pazaryerlerine stok ve fiyat <b>her gün otomatik</b> gönderilir (platform 5-cron sınırı nedeniyle günlük). Sipariş çekme açıksa pazaryeri siparişleri de otomatik ERP'ye düşürülür. İstediğinizde "Şimdi Çalıştır" ile elle de tetikleyebilirsiniz.</p>
        <div className="mb-3 flex flex-wrap gap-3">
          {[["trendyol", "Trendyol"], ["n11", "n11"], ["ticimax", "Ticimax"]].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" data-testid={`auto-sync-${k}`} checked={provList.includes(k)} onChange={() => toggleProvider(k)} className="h-4 w-4 rounded border-slate-300" />
              {lbl}
            </label>
          ))}
        </div>
        <label className="mb-3 flex items-center gap-1.5 text-sm">
          <input type="checkbox" data-testid="auto-sync-pull-orders" checked={auto.pull_orders !== false} onChange={(e) => setAuto({ ...auto, pull_orders: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
          Siparişleri de otomatik çek (pazaryerinden ERP'ye)
        </label>
        {auto.last_run && <p className="mb-2 text-[11px] text-slate-400" data-testid="auto-sync-lastrun">Son çalışma: {new Date(auto.last_run).toLocaleString("tr-TR")}</p>}
        <div className="flex flex-wrap gap-2">
          <Button data-testid="auto-sync-save" onClick={autoSave} disabled={autoSaving} className="bg-brand-green text-white hover:bg-brand-green-600">
            {autoSaving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Kaydet
          </Button>
          <Button data-testid="auto-sync-run" onClick={autoRun} disabled={autoRunning} variant="outline">
            {autoRunning ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Zap className="mr-1.5 h-4 w-4" />} Şimdi Çalıştır
          </Button>
        </div>
      </div>
    </>
  );
}
